import React, { useMemo } from 'react';
import { Box, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import { RegionDto } from '@vatos-pas/common';
import { UserInterface } from 'services/regions';

import { FormToolbar } from 'components/standalone';
import { CostStructureFields } from './CostStructureFields';
import { SupervisorsSection } from './SupervisorsSection';
import { RegionFormValues } from '../containers/useRegionContainer';
import { validateRegion } from '../views/regionValidation';
import { createValidationResolver, normalizeFormNumber } from 'utils';

interface RegionFormProps {
  mode: 'create' | 'edit' | 'show';
  initialValues?: RegionDto | null;
  onSubmit?: (values: RegionFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  supervisors?: UserInterface[];
  supervisorsLoading?: boolean;
}

const getDefaultValues = (
  initialValues?: RegionDto | null,
): RegionFormValues => ({
  name: initialValues?.name || '',
  phaseStructureType: initialValues?.phaseStructureType || 'Drywall',
  // Drywall fields
  dwHangingCostPerBoard: initialValues?.dwHangingCostPerBoard ?? undefined,
  dwFinishingCostPerBoard: initialValues?.dwFinishingCostPerBoard ?? undefined,
  dwSprayingCostPerBoard: initialValues?.dwSprayingCostPerBoard ?? undefined,
  // DrywallAndPaint fields
  dwpHangingCostPerBoard: initialValues?.dwpHangingCostPerBoard ?? undefined,
  dwpFinishCostPerBoard: initialValues?.dwpFinishCostPerBoard ?? undefined,
  dwpPrimeCostPerSqft: initialValues?.dwpPrimeCostPerSqft ?? undefined,
  dwpTrim1CostPerSqft: initialValues?.dwpTrim1CostPerSqft ?? undefined,
  dwpBump1CostPerBoard: initialValues?.dwpBump1CostPerBoard ?? undefined,
  dwpInteriorPaintCostPerSqft:
    initialValues?.dwpInteriorPaintCostPerSqft ?? undefined,
  dwpBump2CostPerBoard: initialValues?.dwpBump2CostPerBoard ?? undefined,
  dwpTrim2CostPerSqft: initialValues?.dwpTrim2CostPerSqft ?? undefined,
  dwpFinalPaintCostPerSqft:
    initialValues?.dwpFinalPaintCostPerSqft ?? undefined,
  dwpBump3CostPerBoard: initialValues?.dwpBump3CostPerBoard ?? undefined,
  dwpCMBluetapeCostPerSqft:
    initialValues?.dwpCMBluetapeCostPerSqft ?? undefined,
  dwpFinalBluetapeCostPerSqft:
    initialValues?.dwpFinalBluetapeCostPerSqft ?? undefined,
  exteriorOnePercentage: normalizeFormNumber(
    initialValues?.exteriorOnePercentage,
  ),
  exteriorTwoPercentage: normalizeFormNumber(
    initialValues?.exteriorTwoPercentage,
  ),
  // Common fields
  repairsRate: initialValues?.repairsRate ?? undefined,
  pieceworkPercentage: normalizeFormNumber(initialValues?.pieceworkPercentage),
  specialJobsPercentage: normalizeFormNumber(
    initialValues?.specialJobsPercentage,
  ),
  garageCostFixed: initialValues?.garageCostFixed ?? undefined,
  highCeilingPerBoard: initialValues?.highCeilingPerBoard ?? undefined,
  screwInspectionPerBoard: initialValues?.screwInspectionPerBoard ?? undefined,
  interiorOptionsPercentage: normalizeFormNumber(
    initialValues?.interiorOptionsPercentage,
  ),
  exteriorOptionsPercentage: normalizeFormNumber(
    initialValues?.exteriorOptionsPercentage,
  ),
  trimOptionPercentage: normalizeFormNumber(
    initialValues?.trimOptionPercentage,
  ),
  allowPayoutAmountEditing: initialValues?.allowPayoutAmountEditing ?? false,
});

export const RegionForm: React.FC<RegionFormProps> = ({
  mode,
  initialValues,
  onSubmit,
  loading,
  onGoBack,
  supervisors = [],
  supervisorsLoading = false,
}) => {
  const classes = useStyles();
  const isCreateMode = mode === 'create';
  const isReadOnly = mode === 'show';

  const formValues = useMemo(
    () => (initialValues ? getDefaultValues(initialValues) : undefined),
    [initialValues],
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegionFormValues>({
    defaultValues: getDefaultValues(initialValues),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<RegionFormValues>(validateRegion),
    mode: 'onBlur',
  });

  const phaseStructureType = watch('phaseStructureType');

  const onFormSubmit = useMemo(
    () =>
      onSubmit
        ? handleSubmit(async data => {
            await onSubmit(data);
          })
        : undefined,
    [handleSubmit, onSubmit],
  );

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                disabled={isReadOnly}
                className={classes.input}
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
        </Box>

        {!isCreateMode && (
          <SupervisorsSection
            supervisors={supervisors}
            loading={supervisorsLoading}
          />
        )}

        <CostStructureFields
          control={control}
          phaseStructureType={phaseStructureType}
          errors={errors}
          isEditMode={!isCreateMode}
          disabled={loading || isReadOnly}
          regionId={initialValues?.id}
        />
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={isReadOnly ? undefined : onFormSubmit}
        saveLoading={loading}
        showSave={!isReadOnly}
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {
    margin: '0px 15px',
  },
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

export default RegionForm;
